import { useLazyQuery } from '@apollo/client';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import { DataExportOneOffExportDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ControlledTabs from 'src/components/controlled-tabs/ControlledTabs';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import useTabs from '@/hooks/useTabs';

import useDataExportStore from './tabs/data-export/dataExportStore';

type Props = {
  activeTabId?:
    | 'approvals'
    | 'audit'
    | 'authentication'
    | 'colours'
    | 'customRoles'
    | 'dataExport'
    | 'dataImport'
    | 'departments'
    | 'entities'
    | 'externalApi'
    | 'groups'
    | 'modules'
    | 'regulatoryFeed'
    | 'sso'
    | 'notifications'
    | 'tags'
    | 'taxonomy'
    | 'users'
    | 'webhooks';
};

const Page: FC<Props> = ({ activeTabId }) => {
  const { i18n: taxonomy } = useTranslation('taxonomy');
  const { t } = useTranslation('common');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');

  const { setDataExportLoading, setDataExportResult, setDataExportError } =
    useDataExportStore();

  const [
    dataExport,
    {
      loading: dataExportLoading,
      data: dataExportResult,
      error: dataExportError,
    },
  ] = useLazyQuery(DataExportOneOffExportDocument, {
    fetchPolicy: 'no-cache',
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDataExportLoading(dataExportLoading), [dataExportLoading]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDataExportResult(dataExportResult), [dataExportResult]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setDataExportError(dataExportError), [dataExportError]);

  const tabs = useTabs({
    parentType: Parent_Type_Enum.Settings,
    parent: undefined,
    hrefRoot: '/settings',
  });
  const title = taxonomy.format(t('setting_other'), 'capitalize');
  const isDataExportTab = activeTabId === 'dataExport';

  // Object-level toolbar (CUS-175) — flag-gated. When on, it replaces the
  // PageLayout header strip; the Data export tab's one-off download is surfaced
  // as a primary action gated on `read:data_export` (mirroring the legacy
  // <Permission> gate). Other tabs render a title-only toolbar. The legacy
  // `actions` block below stays as the flag-off fallback.
  const toolbarConfig: ObjectToolbarConfig = useMemo(() => {
    const config: ObjectToolbarConfig = { header: { title } };
    if (isDataExportTab) {
      config.actions = {
        primary: [
          {
            id: 'data-export-download',
            label: t('dataExport.download'),
            iconName: 'download-01',
            onClick: () => {
              void dataExport();
            },
            disabled: dataExportLoading,
            permission: 'read:data_export',
          },
        ],
      };
    }

    return config;
  }, [title, isDataExportTab, dataExportLoading, dataExport, t]);

  return (
    <PageLayout
      title={title}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        isDataExportTab ? (
          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Permission permission={'read:data_export'}>
              <Button
                iconName={'download'}
                onClick={async () => {
                  await dataExport();
                }}
                disabled={dataExportLoading}
              >
                {t('dataExport.download')}
              </Button>
            </Permission>
          </SpaceBetween>
        ) : undefined
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
      />
    </PageLayout>
  );
};

export default Page;
